/**
 * EXT-06: Extractor de Coordenadas de Texto Libre
 * 
 * Extrae coordenadas UTM de campos de texto/descripción en documentos PTEL.
 * Complementa la extracción tabular con detección en texto narrativo.
 * 
 * Patrones soportados:
 * - T1: Etiquetas explícitas "X: 524538 Y: 4229920"
 * - T2: Formato UTM con etiqueta "Coordenadas UTM: 524538, 4229920"
 * - T3: Separador slash "524538/4229920"
 * - T4: Asignación "X=524538 Y=4229920"
 * - T5: Paréntesis "(524538, 4229920)"
 * - T6: Par numérico simple en rangos UTM
 * - T7: Formato europeo embebido "524.538, 4.229.920"
 * 
 * @author MapWizard (Claude)
 * @created 2025-12-16
 * @task EXT-06
 */

import { RANGOS_ANDALUCIA } from './coordinateNormalizer';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Patrón de coordenadas detectado en texto libre */
export type FreeTextCoordPattern =
  | 'LABEL_XY'           // T1: "X: 524538 Y: 4229920"
  | 'UTM_LABELED'        // T2: "Coordenadas UTM: 524538, 4229920"
  | 'SLASH_SEPARATED'    // T3: "524538/4229920"
  | 'ASSIGNMENT'         // T4: "X=524538 Y=4229920"
  | 'PARENTHESIS'        // T5: "(524538, 4229920)"
  | 'NUMERIC_PAIR'       // T6: "524538 4229920" en rangos UTM
  | 'EUROPEAN_EMBEDDED'  // T7: "524.538, 4.229.920"
  | 'PUNTO_COMA'         // T8: "524538; 4229920"
  | 'UNKNOWN';

/** Nivel de confianza en la extracción */
export type ExtractionConfidence = 'ALTA' | 'MEDIA' | 'BAJA';

/** Coordenada extraída de texto libre */
export interface ExtractedCoordinate {
  /** Valor X (UTM Este) */
  x: number;
  /** Valor Y (UTM Norte) */
  y: number;
  /** Texto original del que se extrajo */
  originalText: string;
  /** Posición de inicio en el texto */
  startIndex: number;
  /** Posición de fin en el texto */
  endIndex: number;
  /** Patrón detectado */
  pattern: FreeTextCoordPattern;
  /** Confianza de la extracción */
  confidence: ExtractionConfidence;
  /** Warnings durante extracción */
  warnings: string[];
}

/** Resultado de extracción de coordenadas de texto libre */
export interface FreeTextExtractionResult {
  /** Coordenadas extraídas (puede ser múltiples) */
  coordinates: ExtractedCoordinate[];
  /** Texto procesado */
  inputText: string;
  /** Estadísticas */
  stats: {
    totalPatterns: number;
    highConfidence: number;
    mediumConfidence: number;
    lowConfidence: number;
  };
}

// ============================================================================
// CONSTANTES - REGEX ORDENADAS POR ESPECIFICIDAD
// ============================================================================

/**
 * Expresiones regulares para detección de patrones.
 * IMPORTANTE: El orden importa - patrones más específicos primero.
 */
const PATTERN_DEFINITIONS: Array<{
  name: FreeTextCoordPattern;
  regex: RegExp;
  priority: number;
}> = [
  // T2: "Coordenadas UTM: 524538, 4229920" - MÁS ESPECÍFICO (tiene palabra clave)
  {
    name: 'UTM_LABELED',
    regex: /\b(?:coord(?:enadas?)?\.?\s*(?:utm|geogr[aá]ficas?)?)\s*[:=]?\s*([\d.,]+)\s*[,;/]\s*([\d.,]+)/gi,
    priority: 1,
  },
  
  // T4: "X=524538 Y=4229920" - Específico con = (antes de T1 que usa :)
  {
    name: 'ASSIGNMENT',
    regex: /\bx\s*=\s*([\d.,]+)\s*[,;]?\s*y\s*=\s*([\d.,]+)/gi,
    priority: 2,
  },
  
  // T1: "X: 524538 Y: 4229920" - Etiquetas con :
  {
    name: 'LABEL_XY',
    regex: /\bx\s*:\s*([\d.,]+)\s*[,;]?\s*y\s*:\s*([\d.,]+)/gi,
    priority: 3,
  },
  
  // T5: "(524538, 4229920)" - Paréntesis
  {
    name: 'PARENTHESIS',
    regex: /\(\s*([\d.,]+)\s*[,;]\s*([\d.,]+)\s*\)/g,
    priority: 4,
  },
  
  // T7: "524.538, 4.229.920" (punto miles, coma decimal opcional)
  {
    name: 'EUROPEAN_EMBEDDED',
    regex: /\b(\d{1,3}(?:\.\d{3})+(?:,\d+)?)\s*[,;]\s*(\d{1,3}(?:\.\d{3})+(?:,\d+)?)\b/g,
    priority: 5,
  },
  
  // T3: "524538/4229920" (slash como separador)
  {
    name: 'SLASH_SEPARATED',
    regex: /\b([\d.,]+)\s*\/\s*([\d.,]+)\b/g,
    priority: 6,
  },
  
  // T8: "524538; 4229920" (punto y coma)
  {
    name: 'PUNTO_COMA',
    regex: /\b(\d{5,7}(?:[.,]\d+)?)\s*;\s*(\d{6,7}(?:[.,]\d+)?)\b/g,
    priority: 7,
  },
  
  // T6: Par numérico en rangos UTM (5-6 dígitos + 7 dígitos)
  {
    name: 'NUMERIC_PAIR',
    regex: /\b(\d{5,6}(?:[.,]\d+)?)\s+(\d{7}(?:[.,]\d+)?)\b/g,
    priority: 8,
  },
];

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

/**
 * Normaliza un valor numérico extraído de texto libre.
 * Maneja formatos europeos, espacios como separadores de miles, etc.
 */
function normalizeExtractedValue(value: string): number | null {
  if (!value || typeof value !== 'string') return null;
  
  // Limpiar espacios
  let cleaned = value.trim().replace(/\s+/g, '');
  
  // Si está vacío después de limpiar
  if (!cleaned) return null;
  
  // Detectar formato europeo (punto miles, coma decimal): "524.538,29"
  if (/^\d{1,3}(?:\.\d{3})+(?:,\d+)?$/.test(cleaned)) {
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }
  // Formato europeo solo decimal: "524538,29"
  else if (/^\d+,\d+$/.test(cleaned)) {
    cleaned = cleaned.replace(',', '.');
  }
  
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/**
 * Valida si un par de valores están en rangos UTM de Andalucía.
 */
function isValidUTMPair(x: number, y: number): boolean {
  const xValid = x >= RANGOS_ANDALUCIA.UTM.X_MIN && x <= RANGOS_ANDALUCIA.UTM.X_MAX;
  const yValid = y >= RANGOS_ANDALUCIA.UTM.Y_MIN && y <= RANGOS_ANDALUCIA.UTM.Y_MAX;
  return xValid && yValid;
}

/**
 * Detecta si X e Y están intercambiadas y las corrige.
 * Devuelve también si hubo intercambio para generar warning.
 */
function detectAndFixSwap(x: number, y: number): { x: number; y: number; swapped: boolean } {
  // Caso normal: X en rango X, Y en rango Y
  if (isValidUTMPair(x, y)) {
    return { x, y, swapped: false };
  }
  
  // Probar intercambiando
  if (isValidUTMPair(y, x)) {
    return { x: y, y: x, swapped: true };
  }
  
  // No se puede validar - devolver sin cambios
  return { x, y, swapped: false };
}

/**
 * Verifica si dos rangos se solapan.
 */
function rangesOverlap(
  start1: number, end1: number,
  start2: number, end2: number
): boolean {
  return start1 < end2 && start2 < end1;
}

/**
 * Calcula confianza basada en el patrón y contexto.
 */
function calculateConfidence(
  pattern: FreeTextCoordPattern,
  x: number,
  y: number
): ExtractionConfidence {
  // Patrones con etiquetas explícitas = alta confianza
  if (['LABEL_XY', 'UTM_LABELED', 'ASSIGNMENT'].includes(pattern)) {
    return 'ALTA';
  }
  
  // Paréntesis con valores en rango = media
  if (pattern === 'PARENTHESIS' && isValidUTMPair(x, y)) {
    return 'MEDIA';
  }
  
  // Formato europeo embebido = media
  if (pattern === 'EUROPEAN_EMBEDDED') {
    return 'MEDIA';
  }
  
  // Par numérico simple = baja (puede ser cualquier cosa)
  if (pattern === 'NUMERIC_PAIR') {
    if (isValidUTMPair(x, y)) {
      return 'MEDIA';
    }
    return 'BAJA';
  }
  
  // Slash y punto y coma = media
  if (['SLASH_SEPARATED', 'PUNTO_COMA'].includes(pattern)) {
    return 'MEDIA';
  }
  
  return 'BAJA';
}

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Extrae coordenadas UTM de texto libre.
 * 
 * @param text - Texto a analizar
 * @param options - Opciones de extracción
 * @returns Resultado con coordenadas encontradas
 * 
 * @example
 * const result = extractCoordinatesFromFreeText(
 *   "El centro de salud está en X: 524538 Y: 4229920"
 * );
 * // result.coordinates[0] = { x: 524538, y: 4229920, pattern: 'LABEL_XY', ... }
 */
export function extractCoordinatesFromFreeText(
  text: string,
  options: {
    /** Incluir matches de baja confianza */
    includeLowConfidence?: boolean;
    /** Validar rangos UTM de Andalucía */
    validateRanges?: boolean;
  } = {}
): FreeTextExtractionResult {
  const {
    includeLowConfidence = false,
    validateRanges = true,
  } = options;
  
  const result: FreeTextExtractionResult = {
    coordinates: [],
    inputText: text,
    stats: {
      totalPatterns: 0,
      highConfidence: 0,
      mediumConfidence: 0,
      lowConfidence: 0,
    },
  };
  
  if (!text || typeof text !== 'string') {
    return result;
  }
  
  // Almacenar rangos ya usados para evitar solapamientos
  const usedRanges: Array<{ start: number; end: number }> = [];
  
  // Procesar patrones en orden de prioridad
  for (const { name: patternName, regex } of PATTERN_DEFINITIONS) {
    // Resetear lastIndex para cada regex
    regex.lastIndex = 0;
    
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      const [fullMatch, value1, value2] = match;
      const startIndex = match.index;
      const endIndex = startIndex + fullMatch.length;
      
      // Verificar si este rango se solapa con alguno ya usado
      const overlaps = usedRanges.some(range => 
        rangesOverlap(startIndex, endIndex, range.start, range.end)
      );
      
      if (overlaps) {
        continue;
      }
      
      // Normalizar valores
      const normalized1 = normalizeExtractedValue(value1);
      const normalized2 = normalizeExtractedValue(value2);
      
      if (normalized1 === null || normalized2 === null) {
        continue;
      }
      
      // Determinar X e Y (pueden estar intercambiadas)
      let x = normalized1;
      let y = normalized2;
      const warnings: string[] = [];
      
      // Detectar e intercambiar si es necesario
      const swapResult = detectAndFixSwap(x, y);
      x = swapResult.x;
      y = swapResult.y;
      
      if (swapResult.swapped) {
        warnings.push(`Coordenadas intercambiadas: original (${normalized1}, ${normalized2}) → (${x}, ${y})`);
      }
      
      // Validar rangos si está habilitado
      if (validateRanges && !isValidUTMPair(x, y)) {
        continue;
      }
      
      // Calcular confianza
      const confidence = calculateConfidence(patternName, x, y);
      
      // Filtrar baja confianza si no está habilitado
      if (confidence === 'BAJA' && !includeLowConfidence) {
        continue;
      }
      
      // Marcar rango como usado
      usedRanges.push({ start: startIndex, end: endIndex });
      
      // Añadir resultado
      const extracted: ExtractedCoordinate = {
        x,
        y,
        originalText: fullMatch,
        startIndex,
        endIndex,
        pattern: patternName,
        confidence,
        warnings,
      };
      
      result.coordinates.push(extracted);
      result.stats.totalPatterns++;
      
      if (confidence === 'ALTA') result.stats.highConfidence++;
      else if (confidence === 'MEDIA') result.stats.mediumConfidence++;
      else result.stats.lowConfidence++;
    }
  }
  
  // Ordenar por posición en el texto
  result.coordinates.sort((a, b) => a.startIndex - b.startIndex);
  
  return result;
}

/**
 * Versión simplificada que devuelve solo el primer par de coordenadas encontrado.
 * Útil para campos de descripción donde se espera una sola ubicación.
 * 
 * @param text - Texto a analizar
 * @returns Par de coordenadas o null si no se encuentra
 */
export function extractFirstCoordinate(
  text: string
): { x: number; y: number; confidence: ExtractionConfidence } | null {
  const result = extractCoordinatesFromFreeText(text, {
    includeLowConfidence: true,
    validateRanges: true,
  });
  
  if (result.coordinates.length === 0) {
    return null;
  }
  
  // Devolver el de mayor confianza
  const sorted = [...result.coordinates].sort((a, b) => {
    const order = { ALTA: 0, MEDIA: 1, BAJA: 2 };
    return order[a.confidence] - order[b.confidence];
  });
  
  const best = sorted[0];
  return {
    x: best.x,
    y: best.y,
    confidence: best.confidence,
  };
}

/**
 * Detecta si un texto contiene coordenadas UTM.
 * Útil para filtrado rápido antes de procesamiento completo.
 * 
 * @param text - Texto a analizar
 * @returns true si parece contener coordenadas
 */
export function hasCoordinatesInText(text: string): boolean {
  if (!text || typeof text !== 'string') return false;
  
  // Indicadores rápidos de coordenadas
  const quickIndicators = [
    /\bx\s*[:=]/i,                    // "X:" o "X="
    /\by\s*[:=]/i,                    // "Y:" o "Y="
    /\bcoord(?:enadas?)?\b/i,         // "coordenadas"
    /\butm\b/i,                       // "UTM"
    /\b\d{5,6}[,;/]\s*\d{7}\b/,       // "524538, 4229920"
    /\(\s*\d{5,6}\s*[,;]\s*\d{7}\s*\)/, // "(524538, 4229920)"
  ];
  
  return quickIndicators.some(pattern => pattern.test(text));
}

// ============================================================================
// INTEGRACIÓN CON EXTRACTOR DE DOCUMENTOS
// ============================================================================

/**
 * Procesa un campo de texto buscando coordenadas para complementar
 * datos de infraestructura que no tienen columnas X/Y explícitas.
 * 
 * @param fieldValue - Valor del campo (descripción, observaciones, etc.)
 * @param fieldName - Nombre del campo para logging
 * @returns Coordenadas extraídas o null
 */
export function extractCoordinatesFromField(
  fieldValue: string,
  fieldName?: string
): { x: number; y: number; source: string; confidence: ExtractionConfidence } | null {
  const coord = extractFirstCoordinate(fieldValue);
  
  if (!coord) return null;
  
  return {
    x: coord.x,
    y: coord.y,
    source: fieldName ? `Campo "${fieldName}"` : 'Texto libre',
    confidence: coord.confidence,
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  extractCoordinatesFromFreeText,
  extractFirstCoordinate,
  hasCoordinatesInText,
  extractCoordinatesFromField,
};
